import { clearUser } from "@store/auth/userSlice";
import {
	setLoadingBusinessUnits,
	setSelectedBusinessUnit,
} from "@store/modules/accountManagement/businessUnitsSlice";
import axios from "axios";

const handleUnauthorized = (dispatch, navigate) => {
	if (dispatch && navigate) {
		dispatch(clearUser());
		navigate("/login");
	} else {
		console.error("Dispatch or navigate function is missing.");
	}
};
export const fetchBusinessUnits = async (
	token,
	dispatch,
	navigate,
	page,
	perPage,
	searchTerms,
	paginate = 1,
	signal = null,
) => {
	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
		},
		params: {
			page: page,
			per_page: perPage,
			name: searchTerms?.name || null,
			country_name: searchTerms?.country_name || null,
			paginate: paginate,
		},
		signal: signal,
	};
	try {
		const response = await axios.get(
			`${import.meta.env.VITE_API_BACKEND}api/business-units`,
			config,
		);
		let data;
		if (!response.data.data || response.data.data.length === 0) {
			data = {
				data: [],
				totalItems: 0,
				page: paginate === 1 ? page : 1,
				perPage: paginate === 1 ? perPage : 10,
			};
		} else {
			const totalItems = response.data?.total;
			data = {
				data: response.data.data,
				totalItems: totalItems,
				page: paginate === 1 ? page : 1,
				perPage: paginate === 1 ? perPage : 10,
			};
		}
		return data;
	} catch (error) {
		if (error.response && error.response.status === 401) {
			handleUnauthorized(dispatch, navigate);
		} else {
			console.error("Error fetching BusinessUnits data:", error);
		}
	}
};
export const deleteBusinessUnits = async (id, token, dispatch, navigate) => {
	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	};

	try {
		const response = await axios.delete(
			`${import.meta.env.VITE_API_BACKEND}api/business-units/${id}`,
			config,
		);

		if (response.status === 200 || response.status === 202 || response.status === 204) {
			return {
				status: "success",
				message: "Business unit deleted successfully.",
			};
		} else {
			return {
				status: "error",
				message: response.data?.message || "An unexpected error occurred.",
			};
		}
	} catch (error) {
		if (error.response) {
			switch (error.response.status) {
				case 401:
					handleUnauthorized(dispatch, navigate);
					break;
				case 404:
					return { status: "not_found", message: "Not found." };
				case 405:
					return {
						status: "method_not_allowed",
						message: "Method Not Allowed",
					};
				case 400:
					return {
						status: "error",
						message: error.response.data?.message || "An error occurred.",
					};
				default:
					return { status: "error", message: "An error occurred." };
			}
		} else {
			return { status: "error", message: "Network error" };
		}
	}
};

export const createBusinessUnit = async (data, token, dispatch, navigate) => {
	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json",
		},
	};
	try {
		const response = await axios.post(
			`${import.meta.env.VITE_API_BACKEND}api/business-units`,
			data,
			config,
		);
		return response.data;
	} catch (error) {
		if (error.response && error.response.status === 401) {
			handleUnauthorized(dispatch, navigate);
		} else if (error.response && error.response.status === 422) {
			// Return the validation error from the response
			return { errors: error.response.data.errors };
		} else {
			console.error("Error creating business unit:", error);
		}
		return null;
	}
};
export const updateBusinessUnit = async (id, data, token, dispatch, navigate) => {
	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json",
		},
	};
	try {
		const response = await axios.put(
			`${import.meta.env.VITE_API_BACKEND}api/business-units/${id}`,
			data,
			config,
		);
		return response.data;
	} catch (error) {
		if (error.response && error.response.status === 401) {
			handleUnauthorized(dispatch, navigate);
		} else if (error.response && error.response.status === 422) {
			// Return the validation error from the response
			return { errors: error.response.data.errors };
		} else {
			console.error("Error updating businessunit:", error);
		}
		return null;
	}
};
export const fetchAllBusinessUnits = async (
	token,
	dispatch,
	navigate,
	paginate = 0,
	signal = null,
) => {
	if (!token) {
		console.error("No token provided, cannot fetch business units.");
		return;
	}
	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
		},
		params: {
			paginate: paginate,
		},
		signal: signal,
	};
	try {
		const response = await axios.get(
			`${import.meta.env.VITE_API_BACKEND}api/business-units`,
			config,
		);
		return response.data || [];
	} catch (error) {
		if (error.response && error.response.status === 401) {
			handleUnauthorized(dispatch, navigate);
		} else {
			console.error("Error fetching BusinessUnits data:", error);
		}
	}
};
export const fetchBusinessUnitById = async (id, token, dispatch, navigate) => {
	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	};
	try {
		const response = await axios.get(
			`${import.meta.env.VITE_API_BACKEND}api/business-units/${id}`,
			config,
		);
		return response.data.data;
	} catch (error) {
		if (error.response && error.response.status === 401) {
			console.error("Unauthorized error, token might be invalid:", error.response);
			handleUnauthorized(dispatch, navigate);
		} else {
			console.error("Error fetching Business Unit data:", error);
		}
	}
};
export const fetchBusinessUnitDetails = async (id, token, dispatch, navigate) => {
	await dispatch(setSelectedBusinessUnit(id));
	await dispatch(setLoadingBusinessUnits(true));
	try {
		const data = await fetchBusinessUnitById(id, token, dispatch, navigate);
		dispatch(setSelectedBusinessUnit(data));
	} catch (error) {
		console.error("Error fetching data:", error);
	} finally {
		dispatch(setLoadingBusinessUnits(false));
	}
};
